import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONSENT_KEY = 'phoneb2b_cookie_consent';

const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, cannot be disabled
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        // Check if user has already given consent
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) {
            // Show banner after a short delay for better UX
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const saveConsent = async (consentData) => {
        const consentRecord = {
            ...consentData,
            timestamp: new Date().toISOString(),
            version: '1.0'
        };
        
        // Save to localStorage
        localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));
        setIsVisible(false);
        
        // If user is authenticated, also save to server
        const token = localStorage.getItem('token');
        if (token) {
            try {
                await axios.post(
                    `${API_URL}/api/user/cookie-consent`,
                    consentData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('Cookie consent saved to server');
            } catch (error) {
                console.error('Failed to save consent to server:', error);
            }
        }
        
        // Here you would typically initialize analytics/marketing scripts
        if (consentData.analytics) {
            console.log('Analytics cookies accepted');
        }
        if (consentData.marketing) {
            console.log('Marketing cookies accepted');
        }
    };

    const acceptAll = () => {
        saveConsent({
            necessary: true,
            analytics: true,
            marketing: true,
            acceptedAll: true
        });
    };

    const acceptNecessaryOnly = () => {
        saveConsent({
            necessary: true,
            analytics: false,
            marketing: false,
            acceptedAll: false
        });
    };

    const savePreferences = () => {
        saveConsent({
            ...preferences,
            acceptedAll: false
        });
    };

    if (!isVisible) return null;

    return (
        <>
            {/* Cookie Banner - No blocking backdrop, just the banner at bottom */}
            <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom duration-300">
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                    {/* Header */}
                    <div className="p-6 pb-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Cookie className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900 mb-1">
                                    Nous respectons votre vie privée
                                </h3>
                                <p className="text-sm text-slate-600">
                                    PHONEB2B utilise des cookies pour améliorer votre expérience de navigation, 
                                    analyser le trafic et personnaliser le contenu. Vous pouvez choisir d'accepter 
                                    tous les cookies ou de personnaliser vos préférences.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Details Section (expandable) */}
                    <div className="px-6">
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium mb-4"
                        >
                            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {showDetails ? 'Masquer les détails' : 'Personnaliser mes choix'}
                        </button>

                        {showDetails && (
                            <div className="space-y-4 pb-4 border-t border-slate-100 pt-4">
                                {/* Necessary Cookies */}
                                <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Shield className="w-4 h-4 text-emerald-600" />
                                            <span className="font-medium text-slate-900 text-sm">Cookies essentiels</span>
                                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Requis</span>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Nécessaires au fonctionnement du site (authentification, panier, préférences).
                                            Ces cookies ne peuvent pas être désactivés.
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <div className="w-10 h-6 bg-emerald-500 rounded-full relative cursor-not-allowed">
                                            <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow" />
                                        </div>
                                    </div>
                                </div>

                                {/* Analytics Cookies */}
                                <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-slate-900 text-sm">Cookies analytiques</span>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Nous permettent de comprendre comment vous utilisez le site pour l'améliorer 
                                            (pages visitées, temps passé, erreurs rencontrées).
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <button
                                            onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                                            className={`w-10 h-6 rounded-full relative transition-colors ${
                                                preferences.analytics ? 'bg-blue-500' : 'bg-slate-300'
                                            }`}
                                        >
                                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                                                preferences.analytics ? 'right-1' : 'left-1'
                                            }`} />
                                        </button>
                                    </div>
                                </div>

                                {/* Marketing Cookies */}
                                <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-slate-900 text-sm">Cookies marketing</span>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Utilisés pour vous proposer des publicités pertinentes et mesurer 
                                            l'efficacité de nos campagnes marketing.
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <button
                                            onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                                            className={`w-10 h-6 rounded-full relative transition-colors ${
                                                preferences.marketing ? 'bg-blue-500' : 'bg-slate-300'
                                            }`}
                                        >
                                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                                                preferences.marketing ? 'right-1' : 'left-1'
                                            }`} />
                                        </button>
                                    </div>
                                </div>

                                <p className="text-xs text-slate-400">
                                    Pour plus d'informations, consultez notre{' '}
                                    <a href="/privacy" className="text-blue-600 hover:underline">politique de confidentialité et cookies</a>.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                        <Button
                            variant="outline"
                            onClick={acceptNecessaryOnly}
                            className="flex-1 sm:flex-none border-slate-300 text-slate-700 hover:bg-slate-100"
                        >
                            Refuser les optionnels
                        </Button>
                        {showDetails && (
                            <Button
                                variant="outline"
                                onClick={savePreferences}
                                className="flex-1 sm:flex-none border-blue-300 text-blue-700 hover:bg-blue-50"
                            >
                                Enregistrer mes choix
                            </Button>
                        )}
                        <Button
                            onClick={acceptAll}
                            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Accepter tout
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

// Hook to check cookie consent status
export const useCookieConsent = () => {
    const [consent, setConsent] = useState(null);

    useEffect(() => {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (stored) {
            setConsent(JSON.parse(stored));
        }
    }, []);

    const hasConsent = (type) => {
        if (!consent) return false;
        return consent[type] === true;
    };

    const resetConsent = async () => {
        localStorage.removeItem(CONSENT_KEY);
        
        // Also reset on server if authenticated
        const token = localStorage.getItem('token');
        if (token) {
            try {
                await axios.post(
                    `${API_URL}/api/user/cookie-consent`,
                    { necessary: true, analytics: false, marketing: false, acceptedAll: false },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.error('Failed to reset consent on server:', error);
            }
        }
        
        window.location.reload();
    };

    return { consent, hasConsent, resetConsent };
};

export default CookieConsent;
